import React from 'react';

interface BackgroundProps {
  activePage: string;
}

const Background: React.FC<BackgroundProps> = ({ activePage }) => {
  // 20 pre-distributed petals with varying sizes, left positioning, animation durations, and delays.
  // Using static values prevents hydration mismatches and guarantees stable, performant animations.
  const petals = [
    { left: '5%', size: 10, delay: '0s', duration: '12s', opacity: 0.3, rotate: '12deg' },
    { left: '15%', size: 14, delay: '2s', duration: '15s', opacity: 0.4, rotate: '45deg' },
    { left: '25%', size: 8, delay: '4s', duration: '10s', opacity: 0.25, rotate: '70deg' },
    { left: '35%', size: 12, delay: '1s', duration: '14s', opacity: 0.35, rotate: '15deg' },
    { left: '45%', size: 16, delay: '5s', duration: '18s', opacity: 0.5, rotate: '110deg' },
    { left: '55%', size: 10, delay: '3s', duration: '13s', opacity: 0.3, rotate: '30deg' },
    { left: '65%', size: 12, delay: '7s', duration: '16s', opacity: 0.4, rotate: '85deg' },
    { left: '75%', size: 9, delay: '2s', duration: '11s', opacity: 0.2, rotate: '50deg' },
    { left: '85%', size: 15, delay: '6s', duration: '17s', opacity: 0.45, rotate: '140deg' },
    { left: '95%', size: 11, delay: '0.5s', duration: '12s', opacity: 0.3, rotate: '25deg' },
    // Extra variety layer
    { left: '10%', size: 12, delay: '6s', duration: '14s', opacity: 0.35, rotate: '95deg' },
    { left: '20%', size: 9, delay: '8s', duration: '12s', opacity: 0.25, rotate: '150deg' },
    { left: '30%', size: 15, delay: '3s', duration: '16s', opacity: 0.4, rotate: '40deg' },
    { left: '40%', size: 11, delay: '7s', duration: '13s', opacity: 0.3, rotate: '65deg' },
    { left: '50%', size: 13, delay: '10s', duration: '15s', opacity: 0.45, rotate: '120deg' },
    { left: '60%', size: 8, delay: '1.5s', duration: '11s', opacity: 0.2, rotate: '10deg' },
    { left: '70%', size: 14, delay: '9s', duration: '17s', opacity: 0.4, rotate: '80deg' },
    { left: '80%', size: 10, delay: '4.5s', duration: '12s', opacity: 0.35, rotate: '35deg' },
    { left: '90%', size: 12, delay: '11s', duration: '16s', opacity: 0.3, rotate: '165deg' },
    { left: '98%', size: 16, delay: '2.5s', duration: '19s', opacity: 0.5, rotate: '55deg' },
  ];

  return (
    <div 
      className="fixed inset-0 pointer-events-none select-none overflow-hidden" 
      style={{ zIndex: -10 }}
    >
      <style>{`
        @keyframes sakura-fall {
          0% {
            transform: translateY(-10vh) translateX(0) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(110vh) translateX(120px) rotate(360deg);
            opacity: 0;
          }
        }
        
        .sakura-petal {
          position: absolute;
          background: linear-gradient(135deg, rgba(168, 85, 247, 0.4) 0%, rgba(216, 180, 254, 0.15) 100%);
          border-radius: 150% 0 150% 150%;
          transform-origin: center;
          animation: sakura-fall linear infinite;
          filter: drop-shadow(0 0 4px rgba(168, 85, 247, 0.2));
        }
      `}</style>

      {/* Solid Black Canvas */}
      <div className="absolute inset-0 bg-[#030303]" />

      {/* Atmospheric Violet Radial Glow */}
      <div className="absolute top-[-10%] right-[-10%] w-[80vw] h-[80vw] max-w-[800px] rounded-full bg-purple-900/10 blur-[120px]" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[80vw] h-[80vw] max-w-[800px] rounded-full bg-indigo-900/10 blur-[120px]" />

      {/* Top-Right Cherry Blossom Branch Silhouette */}
      <svg 
        className="absolute top-0 right-0 w-[45vw] h-[45vw] max-w-[550px] min-w-[280px] text-purple-950/15 pointer-events-none transform translate-x-4 -translate-y-4 transition-all duration-1000" 
        viewBox="0 0 100 100" 
        fill="currentColor"
      >
        {/* Main large elegant cherry blossom branch */}
        <path d="M 100,0 C 95,5 82,10 75,8 C 70,7 65,4 60,8 C 55,12 48,15 42,12 C 35,9 28,15 22,20 C 18,24 15,32 10,34 M 75,8 C 72,15 65,18 58,22 C 50,27 45,35 38,38 M 60,8 C 57,5 52,2 48,5 C 43,8 39,12 34,10 C 30,8 25,12 21,15 M 42,12 C 40,18 35,22 30,26 C 25,30 22,38 18,40 M 22,20 C 20,18 16,14 12,18 C 8,22 5,20 2,24" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none" />
        
        {/* Branch leaves/blossom clusters */}
        <circle cx="75" cy="8" r="1.5" />
        <circle cx="72" cy="11" r="1.2" />
        <circle cx="68" cy="9" r="1.8" />
        <circle cx="60" cy="8" r="1.6" />
        <circle cx="58" cy="12" r="1.3" />
        <circle cx="53" cy="10" r="1.5" />
        <circle cx="48" cy="5" r="1.7" />
        <circle cx="42" cy="12" r="1.4" />
        <circle cx="40" cy="15" r="1.2" />
        <circle cx="36" cy="11" r="1.6" />
        <circle cx="30" cy="14" r="1.3" />
        <circle cx="28" cy="18" r="1.5" />
        <circle cx="22" cy="20" r="1.6" />
        <circle cx="18" cy="24" r="1.2" />
        <circle cx="15" cy="22" r="1.4" />
        <circle cx="12" cy="18" r="1.3" />
        <circle cx="10" cy="34" r="1.1" />
        <circle cx="58" cy="22" r="1.4" />
        <circle cx="52" cy="25" r="1.2" />
        <circle cx="45" cy="35" r="1.3" />
        <circle cx="38" cy="38" r="1.5" />
        <circle cx="34" cy="42" r="1.1" />
        <circle cx="30" cy="26" r="1.4" />
        <circle cx="25" cy="30" r="1.2" />
        <circle cx="21" cy="32" r="1.5" />
        <circle cx="18" cy="40" r="1.1" />
        
        {/* Subtle decorative blossom outlines */}
        <path d="M 73.5,6.5 C 73.5,5 75,5 75,6.5 C 75,8 73.5,8 73.5,6.5 Z" fill="currentColor" opacity="0.6" />
        <path d="M 58.5,6.5 C 58.5,5 60,5 60,6.5 C 60,8 58.5,8 58.5,6.5 Z" fill="currentColor" opacity="0.6" />
        <path d="M 40.5,10.5 C 40.5,9 42,9 42,10.5 C 42,12 40.5,12 40.5,10.5 Z" fill="currentColor" opacity="0.6" />
        <path d="M 20.5,18.5 C 20.5,17 22,17 22,18.5 C 22,20 20.5,20 20.5,18.5 Z" fill="currentColor" opacity="0.6" />
      </svg>

      {/* Bottom-Left Cherry Blossom Branch Silhouette */}
      <svg 
        className="absolute bottom-0 left-0 w-[35vw] h-[35vw] max-w-[450px] min-w-[200px] text-purple-950/10 pointer-events-none transform -translate-x-4 translate-y-4 transition-all duration-1000" 
        viewBox="0 0 100 100" 
        fill="currentColor"
      >
        <path d="M 0,100 C 5,95 18,90 25,92 C 30,93 35,96 40,92 C 45,88 52,85 58,88 C 65,91 72,85 78,80 C 82,76 85,68 90,66" stroke="currentColor" strokeWidth="1" strokeLinecap="round" fill="none" />
        <circle cx="25" cy="92" r="1.4" />
        <circle cx="30" cy="93" r="1.1" />
        <circle cx="35" cy="96" r="1.3" />
        <circle cx="40" cy="92" r="1.5" />
        <circle cx="45" cy="88" r="1.2" />
        <circle cx="52" cy="85" r="1.4" />
        <circle cx="58" cy="88" r="1.3" />
        <circle cx="65" cy="91" r="1.2" />
        <circle cx="72" cy="85" r="1.4" />
        <circle cx="78" cy="80" r="1.3" />
        <circle cx="82" cy="76" r="1.1" />
        <circle cx="90" cy="66" r="1.0" />
      </svg>

      {/* Falling Sakura Petals Loop */}
      <div className="absolute inset-0 z-0">
        {petals.map((petal, index) => (
          <div
            key={index}
            className="sakura-petal"
            style={{
              left: petal.left,
              width: `${petal.size}px`,
              height: `${petal.size * 0.75}px`,
              animationDelay: petal.delay,
              animationDuration: petal.duration,
              opacity: petal.opacity,
              transform: `rotate(${petal.rotate})`,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default Background;
